import { Hero } from "../components/Hero";
import { Services } from "../components/Services";
import { ZohoApps } from "../components/ZohoApps";
import PartnersPreview from "../components/Partners";
import { Testimonials } from "../components/Testimonials";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowRight, CheckCircle, Zap, Target, Users, TrendingUp, Shield, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { SEO } from "../components/SEO";

const whyChooseUs = [
  {
    icon: Award,
    title: "Certified Zoho Experts",
    description: "Official Zoho certifications and 5+ years of hands-on experience",
    metric: "15+ Certifications"
  },
  {
    icon: TrendingUp,
    title: "Proven Track Record",
    description: "500+ successful implementations with 98% client satisfaction rate",
    metric: "98% Success Rate"
  },
  {
    icon: Zap,
    title: "Rapid Implementation",
    description: "Streamlined processes for faster deployment with minimal disruption",
    metric: "50% Faster Setup"
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    description: "Bank-grade security implementation with compliance standards",
    metric: "100% Secure"
  },
  {
    icon: Users,
    title: "Dedicated Support",
    description: "24/7 technical support with dedicated project managers",
    metric: "24/7 Available"
  },
  {
    icon: Target,
    title: "ROI Focused",
    description: "Solutions designed to deliver measurable business results",
    metric: "3x ROI Average"
  }
];

const industryStats = [
  {
    number: "500+",
    label: "Projects Delivered",
    description: "Over 500 successful Zoho implementations and automation projects."
  },
  {
    number: "98%",
    label: "Client Satisfaction Rate",
    description: "High satisfaction rate from clients who have achieved their business goals."
  },
  {
    number: "15+",
    label: "Certifications",
    description: "Official Zoho certifications and industry-leading expertise."
  }
];

export default function HomePage() {
  return (
    <>
      <SEO
        title="Optiwizer Technologies – Zoho Implementation Partner & Automation Experts"
        description="Transform your business with certified Zoho implementation, automation, and analytics from Optiwizer Technologies. 500+ projects delivered. Book a free consultation!"
        canonical="https://optiwizer.com/"
        ogImage="/assets/logos/optiwizer-logo.png"
      />
      {/* Hero Section (should contain the only h1) */}
      <Hero />
      {/* Why Choose Us Section */}
      <section className="py-16 lg:py-20 bg-white" aria-labelledby="why-choose-us-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Why Choose Us</Badge>
            <h2 id="why-choose-us-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Your Trusted Zoho Implementation Partner
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              With certified expertise and proven methodologies, we deliver Zoho solutions 
              that transform businesses and drive sustainable growth.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {whyChooseUs.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6 lg:p-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                    <feature.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      {/* Industry Stats Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-blue-600 to-purple-600" aria-labelledby="industry-stats-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h2 id="industry-stats-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Trusted by Businesses Worldwide
            </h2>
            <p className="text-lg lg:text-xl text-blue-100 max-w-3xl mx-auto">
              Our track record speaks for itself. Join hundreds of satisfied clients 
              who have transformed their business with our Zoho expertise.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {industryStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-lg lg:text-xl font-semibold text-blue-100 mb-2">
                  {stat.label}
                </div>
                <div className="text-sm lg:text-base text-blue-200">
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Services Preview Section */}
      <section aria-label="Services Preview">
        <Services />
      </section>
      {/* Zoho Apps Overview Section */}
      <section aria-label="Zoho Apps Overview">
        <ZohoApps />
      </section>
      {/* Partners Preview Section */}
      <section aria-label="Partners Preview">
        <PartnersPreview />
      </section>
      {/* Client Success Story Section */}
      <section className="py-16 lg:py-20 bg-gray-50" aria-labelledby="success-story-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="space-y-6">
              <Badge className="bg-green-100 text-green-800">Success Story</Badge>
              <h2 id="success-story-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
                How We Helped TechCorp Scale Their Operations
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                TechCorp was struggling with disconnected systems and manual processes. 
                Our comprehensive Zoho implementation unified their sales, marketing, 
                and operations, resulting in a 300% increase in productivity.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">300% Productivity Increase</div>
                    <div className="text-gray-600">Streamlined workflows and automation</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">50% Cost Reduction</div>
                    <div className="text-gray-600">Eliminated redundant tools and processes</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">6-Week Implementation</div>
                    <div className="text-gray-600">Fast deployment with zero downtime</div>
                  </div>
                </div>
              </div>
              <Link to="/about">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Read Full Case Study
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <ImageWithFallback 
                src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop"
                alt="Business transformation success story"
                className="rounded-2xl shadow-xl w-full h-[300px] lg:h-[400px] object-cover"
              />
              {/* Floating Success Metrics */}
              <div className="absolute -top-4 -right-4 lg:-top-6 lg:-right-6 bg-white rounded-xl shadow-lg p-4 lg:p-6">
                <div className="text-2xl lg:text-3xl font-bold text-green-600">300%</div>
                <div className="text-sm lg:text-base text-gray-600">Growth</div>
              </div>
              <div className="absolute -bottom-4 -left-4 lg:-bottom-6 lg:-left-6 bg-blue-600 text-white rounded-xl shadow-lg p-4 lg:p-6">
                <div className="text-xl lg:text-2xl font-bold text-white">6 Weeks</div>
                <div className="text-sm lg:text-base text-white">Implementation</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Testimonials Section */}
      <section aria-label="Testimonials">
        <Testimonials />
      </section>
      {/* Final CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-gray-900 to-blue-900" aria-labelledby="final-cta-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="final-cta-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-lg lg:text-xl text-gray-300 mb-8 leading-relaxed">
            Join hundreds of successful businesses that have accelerated their growth 
            with our expert Zoho implementation and automation services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-4">
                Start Your Free Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-4">
                Explore Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}